import { Routes } from '@angular/router';
import { LoginComponent } from '@pkg-views/auth/login.component';
import { AdminLayoutComponent } from '@app/layout/admin-layout/admin-layout.component';
import { DashboardComponent } from '@pkg-views/admin/dashboard/dashboard.component';
import { authGuard } from './guards/is-signed-in.guard';

const titleSuffix = ' | PKG Super Admin';

export const routes: Routes = [
  {
    path: 'login',
    title: 'Connexion' + titleSuffix,
    component: LoginComponent
  },
  {
    path: 'forgot-password',
    title: 'Mot de passe oublié' + titleSuffix,
    loadComponent: () => import('./views/auth/forgot-password.component').then(m => m.ForgotPasswordComponent)
  },
  {
    path: 'set-password',
    title: 'Définir le mot de passe' + titleSuffix,
    loadComponent: () => import('./views/auth/set-password.component').then(m => m.SetPasswordComponent)
  },
  {
    path: 'reset-password',
    title: 'Réinitialiser le mot de passe' + titleSuffix,
    loadComponent: () => import('./views/auth/reset-password.component').then(m => m.ResetPasswordComponent)
  },
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        title: 'Dashboard' + titleSuffix,
        component: DashboardComponent
      },
      {
        path: 'plateforme/rapport-erreurs',
        title: 'Rapport d’erreurs' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/plateforme/error-report.component').then((m) => m.ErrorReportComponent),
      },
      {
        path: 'plateforme/frequentation',
        title: 'Fréquentation du site' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/plateforme/site-visits.component').then((m) => m.SiteVisitsComponent),
      },
      {
        path: 'plateforme/ota-mobile',
        title: 'OTA mobile' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/plateforme/mobile-ota-releases.component').then(
            (m) => m.MobileOtaReleasesComponent,
          ),
      },
      // Référentiels
      {
        path: 'referentiels/themes',
        title: 'Thèmes' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/referentiels/themes/themes-list.component').then(m => m.ThemesListComponent)
      },
      {
        path: 'referentiels/activites',
        title: 'Activités' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/referentiels/activites/activities-list.component').then(m => m.ActivitiesListComponent)
      },
      {
        path: 'referentiels/categories',
        title: 'Catégories' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/referentiels/categories/categories-list.component').then(m => m.CategoriesListComponent)
      },
      {
        path: 'referentiels/villes',
        title: 'Villes' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/referentiels/villes/cities-list.component').then(m => m.CitiesListComponent)
      },
      {
        path: 'referentiels/villes/:id',
        title: 'Modifier la ville' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/referentiels/villes/city-edit.component').then(m => m.CityEditComponent)
      },
      // Mairies démarchées : mêmes écrans que la prospection des structures, filtrés sur les mairies (19/10/2026).
      {
        path: 'collectivites/mairies',
        title: 'Mairies' + titleSuffix,
        data: { listing: 'mairies' },
        loadComponent: () =>
          import('./views/admin/prospection/prospection-prospects-list.component').then(
            (m) => m.ProspectionProspectsListComponent,
          ),
      },
      {
        path: 'collectivites/mairies/:uuid',
        title: 'Mairie' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-prospect-detail.component').then(
            (m) => m.ProspectionProspectDetailComponent,
          ),
      },
      {
        path: 'collectivites/campagnes',
        title: 'Campagnes mairies' + titleSuffix,
        data: { cible: 'mairies' },
        loadComponent: () =>
          import('./views/admin/prospection/prospection-campaigns.component').then(
            (m) => m.ProspectionCampaignsComponent,
          ),
      },
      {
        path: 'collectivites/campagnes/:uuid',
        title: 'Campagne mairies' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-campaign-detail.component').then(
            (m) => m.ProspectionCampaignDetailComponent,
          ),
      },
      {
        path: 'collectivites/import',
        title: 'Import des mairies' + titleSuffix,
        data: { mairies: true },
        loadComponent: () =>
          import('./views/admin/prospection/prospection-research-import.component').then(
            (m) => m.ProspectionResearchImportComponent,
          ),
      },
      {
        path: 'collectivites/communes',
        title: 'Communes inscrites' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/collectivites/city-hub-accounts-list.component').then((m) => m.CityHubAccountsListComponent),
      },
      {
        path: 'collectivites/communes/:uuid',
        title: 'Commune inscrite' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/collectivites/city-hub-account-detail.component').then((m) => m.CityHubAccountDetailComponent),
      },
      {
        path: 'collectivites/demandes',
        title: 'Revendications mairies' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/collectivites/city-hub-claims-list.component').then(
            (m) => m.CityHubClaimsListComponent,
          ),
      },
      {
        path: 'collectivites/demandes/:uuid',
        title: 'Demande collectivité' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/collectivites/city-hub-claim-detail.component').then(
            (m) => m.CityHubClaimDetailComponent,
          ),
      },
      {
        path: 'collectivites/contenus',
        title: 'Actualités communes' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/collectivites/city-hub-articles-list.component').then(
            (m) => m.CityHubArticlesListComponent,
          ),
      },
      {
        path: 'collectivites/contenus/nouveau',
        title: 'Nouvelle actualité commune' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/collectivites/city-hub-article-editor.component').then(
            (m) => m.CityHubArticleEditorComponent,
          ),
      },
      {
        path: 'collectivites/contenus/:uuid',
        title: 'Actualité commune' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/collectivites/city-hub-article-editor.component').then(
            (m) => m.CityHubArticleEditorComponent,
          ),
      },
      // Organisations
      {
        path: 'organisations',
        title: 'Organisations' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/organisations/organisations-list.component').then(m => m.OrganisationsListComponent)
      },
      {
        path: 'organisations/creer',
        title: 'Créer organisation' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/organisations/organisations-assisted-create.component').then(m => m.OrganisationsAssistedCreateComponent)
      },
      {
        path: 'organisations/creer-claim',
        title: 'Créer fiche claim' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/organisations/organisations-claim-create.component').then(m => m.OrganisationsClaimCreateComponent)
      },
      {
        path: 'organisations/fiche-claim/:uuid',
        title: 'Fiche claim' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/organisations/organisations-claim-create.component').then(m => m.OrganisationsClaimCreateComponent)
      },
      {
        path: 'organisations/:uuid',
        title: 'Détail organisation' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/organisations/organization-shell.component').then(m => m.OrganizationShellComponent),
        data: { mode: 'read', backLink: '/admin/organisations' },
        children: [
          { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
          { path: 'sheet', loadComponent: () => import('@pkg-views/admin/organisations/tabs/vitrine/org-sheet/org-sheet.component').then(m => m.OrgSheetComponent) },
          { path: 'dashboard', loadComponent: () => import('@pkg-views/admin/organisations/views/org-dashboard.component').then(m => m.OrgDashboardComponent) },
          // Configuration (30/10/2026) : section à part du sous-menu, hors des onglets Organisation. Les anciennes
          // adresses sous organisation/ y renvoient (déclarées avant la route organisation pour passer en premier).
          { path: 'organisation/modules', redirectTo: 'configuration/modules', pathMatch: 'full' },
          { path: 'organisation/provisioning', redirectTo: 'configuration/modules', pathMatch: 'full' },
          { path: 'organisation/acces', redirectTo: 'configuration/acces', pathMatch: 'full' },
          { path: 'organisation/onboarding', redirectTo: 'configuration/acces', pathMatch: 'full' },
          { path: 'configuration', redirectTo: 'configuration/modules', pathMatch: 'full' },
          { path: 'configuration/modules', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/modules/org-support-modules.component').then(m => m.OrgSupportModulesComponent) },
          {
            path: 'configuration/acces',
            loadComponent: () =>
              import('@pkg-views/admin/organisations/tabs/organisation/pages/access/org-organisation-access.component').then(
                (m) => m.OrgOrganisationAccessComponent,
              ),
          },
          {
            path: 'organisation',
            loadComponent: () => import('@pkg-views/admin/organisations/tabs/organisation/org-organisation.component').then(m => m.OrgOrganisationComponent),
            data: { contentLayout: 'flush' },
            children: [
              { path: '', redirectTo: 'edit', pathMatch: 'full' },
              { path: 'edit', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/edit/org-support-edit.component').then(m => m.OrgSupportEditComponent) },
              { path: 'activities', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/activities/org-support-activities.component').then(m => m.OrgSupportActivitiesComponent) },
              { path: 'lieux', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/locations/org-support-locations.component').then(m => m.OrgSupportLocationsComponent) },
              { path: 'galeries', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/galleries/org-support-galleries.component').then(m => m.OrgSupportGalleriesComponent) },
              { path: 'tarifs', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/pricing/org-support-pricing.component').then(m => m.OrgSupportPricingComponent) },
              { path: 'preview', loadComponent: () => import('@pkg-views/admin/organisations/tabs/vitrine/org-sheet/org-sheet.component').then(m => m.OrgSheetComponent) },
              {
                path: 'avis-verifies-pkg',
                loadComponent: () =>
                  import('@pkg-views/admin/organisations/tabs/organisation/pages/pkg-verified/org-organisation-pkg-verified.component').then(
                    (m) => m.OrgOrganisationPkgVerifiedComponent,
                  ),
              },
            ]
          },
          { path: 'support', redirectTo: 'support/adherents', pathMatch: 'full' },
          { path: 'support/team', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/team/org-support-team.component').then(m => m.OrgSupportTeamComponent) },
          { path: 'support/adherents', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/adherents/org-support-adherents.component').then(m => m.OrgSupportAdherentsComponent) },
          { path: 'support/activities', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/activities/org-support-activities.component').then(m => m.OrgSupportActivitiesComponent) },
          { path: 'support/orders', redirectTo: 'stripe/transactions', pathMatch: 'full' },
          { path: 'support/onboarding', redirectTo: 'configuration/acces', pathMatch: 'full' },
          { path: 'support/impersonation', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/impersonation/org-support-impersonation.component').then(m => m.OrgSupportImpersonationComponent) },
          // contact removed
          {
            path: 'stripe',
            loadComponent: () => import('@pkg-views/admin/organisations/tabs/stripe/org-stripe.component').then(m => m.OrgStripeTabComponent),
            data: { contentLayout: 'flush' },
            children: [
              { path: '', redirectTo: 'stats', pathMatch: 'full' },
              { path: 'stats', loadComponent: () => import('@pkg-views/admin/organisations/tabs/stripe/pages/stats/org-stripe-stats.component').then(m => m.OrgStripeStatsComponent) },
              { path: 'transactions', loadComponent: () => import('@pkg-views/admin/organisations/tabs/stripe/pages/transactions/org-stripe-transactions.component').then(m => m.OrgStripeTransactionsComponent) },
              { path: 'payments-audit', loadComponent: () => import('@pkg-views/admin/organisations/tabs/stripe/pages/payments-audit/org-stripe-payments-audit.component').then(m => m.OrgStripePaymentsAuditComponent) },
            ]
          },
        ]
      },
      // Modération inscriptions (onboarding organisations)
      {
        path: 'onboarding/inscriptions',
        title: 'Demandes d’inscription' + titleSuffix,
        loadComponent: () => import('./views/admin/onboarding/onboarding-organizations-list.component').then(m => m.OnboardingOrganizationsListComponent)
      },
      {
        path: 'prospection',
        title: 'Prospection' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-dashboard.component').then(
            m => m.ProspectionDashboardComponent
          )
      },
      {
        path: 'prospection/seance',
        title: 'Séance de prospection' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-session.component').then(
            m => m.ProspectionSessionComponent
          )
      },
      {
        path: 'prospection/bilan',
        title: 'Bilan de prospection' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-report.component').then(
            m => m.ProspectionReportComponent
          )
      },
      {
        path: 'prospection/import-recherche',
        title: 'Import de recherche' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-research-import.component').then(
            m => m.ProspectionResearchImportComponent
          )
      },
      {
        path: 'prospection/propositions',
        title: 'Propositions de recherche' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-research-batch.component').then(
            m => m.ProspectionResearchBatchComponent
          )
      },
      {
        path: 'prospection/suivi',
        title: 'Suivi des prospects' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-prospects-list.component').then(
            m => m.ProspectionProspectsListComponent
          )
      },
      {
        path: 'prospection/en-attente',
        title: 'Prospects en attente de fiche' + titleSuffix,
        data: { listing: 'pending' },
        loadComponent: () =>
          import('./views/admin/prospection/prospection-prospects-list.component').then(
            m => m.ProspectionProspectsListComponent
          )
      },
      {
        path: 'prospection/exclusions',
        title: 'Exclusions' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-exclusions.component').then(
            m => m.ProspectionExclusionsComponent
          )
      },      {
        path: 'prospection/rappels',
        title: 'Rappels' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-callbacks.component').then(
            m => m.ProspectionCallbacksComponent
          )
      },
      {
        path: 'prospection/agenda',
        title: 'Agenda de prospection' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-agenda.component').then(
            m => m.ProspectionAgendaComponent
          )
      },
      {
        path: 'prospection/villes/:id',
        title: 'Prospection — ville' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-city.component').then(
            m => m.ProspectionCityComponent
          )
      },
      {
        path: 'prospection/campagnes',
        title: 'Campagnes de prospection' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-campaigns.component').then(
            (m) => m.ProspectionCampaignsComponent,
          ),
      },
      {
        path: 'prospection/campagnes/:uuid',
        title: 'Campagne de prospection' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-campaign-detail.component').then(
            (m) => m.ProspectionCampaignDetailComponent,
          ),
      },
      {
        path: 'prospection/prospects/:uuid',
        title: 'Prospect' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/prospection/prospection-prospect-detail.component').then(
            m => m.ProspectionProspectDetailComponent
          )
      },
      {
        path: 'demandes/signalements',
        title: 'Fiches signalées' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/listing-reports/listing-reports-list.component').then(
            m => m.ListingReportsListComponent
          )
      },
      {
        path: 'demandes/ajout-activites',
        title: 'Formulaires vitrine' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/activity-add-requests/activity-add-requests-list.component').then(
            m => m.ActivityAddRequestsListComponent
          )
      },
      {
        path: 'onboarding/inscriptions/:uuid',
        title: 'Validation organisation' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/organisations/organization-shell.component').then(m => m.OrganizationShellComponent),
        data: { mode: 'onboarding', backLink: '/admin/onboarding/inscriptions' },
        children: [
          { path: '', redirectTo: 'configuration/acces', pathMatch: 'full' },
          { path: 'sheet', loadComponent: () => import('@pkg-views/admin/organisations/tabs/vitrine/org-sheet/org-sheet.component').then(m => m.OrgSheetComponent) },
          { path: 'dashboard', loadComponent: () => import('@pkg-views/admin/organisations/views/org-dashboard.component').then(m => m.OrgDashboardComponent) },
          // Configuration (30/10/2026) : section à part du sous-menu, hors des onglets Organisation. Les anciennes
          // adresses sous organisation/ y renvoient (déclarées avant la route organisation pour passer en premier).
          { path: 'organisation/modules', redirectTo: 'configuration/modules', pathMatch: 'full' },
          { path: 'organisation/provisioning', redirectTo: 'configuration/modules', pathMatch: 'full' },
          { path: 'organisation/acces', redirectTo: 'configuration/acces', pathMatch: 'full' },
          { path: 'organisation/onboarding', redirectTo: 'configuration/acces', pathMatch: 'full' },
          { path: 'configuration', redirectTo: 'configuration/modules', pathMatch: 'full' },
          { path: 'configuration/modules', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/modules/org-support-modules.component').then(m => m.OrgSupportModulesComponent) },
          {
            path: 'configuration/acces',
            loadComponent: () =>
              import('@pkg-views/admin/organisations/tabs/organisation/pages/access/org-organisation-access.component').then(
                (m) => m.OrgOrganisationAccessComponent,
              ),
          },
          {
            path: 'organisation',
            loadComponent: () => import('@pkg-views/admin/organisations/tabs/organisation/org-organisation.component').then(m => m.OrgOrganisationComponent),
            data: { contentLayout: 'flush' },
            children: [
              { path: '', redirectTo: 'edit', pathMatch: 'full' },
              { path: 'edit', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/edit/org-support-edit.component').then(m => m.OrgSupportEditComponent) },
              { path: 'activities', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/activities/org-support-activities.component').then(m => m.OrgSupportActivitiesComponent) },
              { path: 'lieux', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/locations/org-support-locations.component').then(m => m.OrgSupportLocationsComponent) },
              { path: 'galeries', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/galleries/org-support-galleries.component').then(m => m.OrgSupportGalleriesComponent) },
              { path: 'tarifs', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/pricing/org-support-pricing.component').then(m => m.OrgSupportPricingComponent) },
              { path: 'preview', loadComponent: () => import('@pkg-views/admin/organisations/tabs/vitrine/org-sheet/org-sheet.component').then(m => m.OrgSheetComponent) },
              {
                path: 'avis-verifies-pkg',
                loadComponent: () =>
                  import('@pkg-views/admin/organisations/tabs/organisation/pages/pkg-verified/org-organisation-pkg-verified.component').then(
                    (m) => m.OrgOrganisationPkgVerifiedComponent,
                  ),
              },
            ]
          },
          { path: 'support', redirectTo: 'support/adherents', pathMatch: 'full' },
          { path: 'support/team', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/team/org-support-team.component').then(m => m.OrgSupportTeamComponent) },
          { path: 'support/adherents', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/adherents/org-support-adherents.component').then(m => m.OrgSupportAdherentsComponent) },
          { path: 'support/activities', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/activities/org-support-activities.component').then(m => m.OrgSupportActivitiesComponent) },
          { path: 'support/orders', redirectTo: 'stripe/transactions', pathMatch: 'full' },
          { path: 'support/onboarding', redirectTo: 'configuration/acces', pathMatch: 'full' },
          { path: 'support/impersonation', loadComponent: () => import('@pkg-views/admin/organisations/support/pages/impersonation/org-support-impersonation.component').then(m => m.OrgSupportImpersonationComponent) },
          // contact removed
          {
            path: 'stripe',
            loadComponent: () => import('@pkg-views/admin/organisations/tabs/stripe/org-stripe.component').then(m => m.OrgStripeTabComponent),
            data: { contentLayout: 'flush' },
            children: [
              { path: '', redirectTo: 'stats', pathMatch: 'full' },
              { path: 'stats', loadComponent: () => import('@pkg-views/admin/organisations/tabs/stripe/pages/stats/org-stripe-stats.component').then(m => m.OrgStripeStatsComponent) },
              { path: 'transactions', loadComponent: () => import('@pkg-views/admin/organisations/tabs/stripe/pages/transactions/org-stripe-transactions.component').then(m => m.OrgStripeTransactionsComponent) },
              { path: 'payments-audit', loadComponent: () => import('@pkg-views/admin/organisations/tabs/stripe/pages/payments-audit/org-stripe-payments-audit.component').then(m => m.OrgStripePaymentsAuditComponent) },
            ]
          },
        ]
      },
      // Documents légaux (conditions/CGU/RGPD)
      {
        path: 'legal-documents',
        title: 'Documents légaux' + titleSuffix,
        loadComponent: () => import('./views/admin/legal/legal-documents-list.component').then(m => m.LegalDocumentsListComponent)
      },
      {
        path: 'legal-documents/:uuid',
        title: 'Document légal' + titleSuffix,
        loadComponent: () => import('./views/admin/legal/legal-document-detail.component').then(m => m.LegalDocumentDetailComponent)
      },
      // Contenu (pages dynamiques + médias)
      {
        path: 'contenu/pages',
        title: 'Pages' + titleSuffix,
        loadComponent: () => import('./views/admin/content/pages/pages-list.component').then(m => m.PagesListComponent)
      },
      {
        path: 'contenu/pages/nouveau',
        title: 'Nouvelle page' + titleSuffix,
        loadComponent: () => import('./views/admin/content/pages/page-editor.component').then(m => m.PageEditorComponent)
      },
      {
        path: 'contenu/pages/:uuid',
        title: 'Édition page' + titleSuffix,
        loadComponent: () => import('./views/admin/content/pages/page-editor.component').then(m => m.PageEditorComponent)
      },
      {
        path: 'contenu/medias',
        title: 'Médiathèque' + titleSuffix,
        loadComponent: () => import('./views/admin/content/media/media-library.component').then(m => m.MediaLibraryComponent)
      },
      // Paiements
      {
        path: 'paiements/transactions',
        title: 'Transactions' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/paiements/transactions-list.component').then(m => m.TransactionsListComponent)
      },
      {
        path: 'paiements/finance',
        title: 'Finance plateforme' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/paiements/finance/platform-finance.component').then(m => m.PlatformFinanceComponent)
      },
      {
        path: 'paiements/ledger',
        title: 'Finance plateforme' + titleSuffix,
        redirectTo: 'paiements/finance',
        pathMatch: 'full'
      },
      {
        path: 'paiements/ledger/organisations/:uuid',
        title: 'Ledger organisation' + titleSuffix,
        loadComponent: () => import('./views/admin/paiements/ledger/ledger-organization.component').then(m => m.LedgerOrganizationComponent)
      },
      {
        path: 'paiements/transactions/:uuid',
        title: 'Détail transaction' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/paiements/transaction-detail.component').then(m => m.TransactionDetailComponent)
      },
      {
        // Liste fusionnée dans Transactions (n° de commande en colonne) : seul le DÉTAIL commande
        // subsiste, ouvert depuis le détail transaction et depuis le hub organisation.
        path: 'paiements/commandes',
        title: 'Transactions' + titleSuffix,
        redirectTo: 'paiements/transactions',
        pathMatch: 'full'
      },
      {
        path: 'paiements/commandes/:uuid',
        title: 'Détail commande' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/paiements/order-detail.component').then(m => m.OrderDetailComponent)
      },
      {
        path: 'paiements/sante',
        title: 'Santé paiements' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/paiements/payments-health/payments-health.component').then(m => m.PaymentsHealthComponent)
      },
      {
        path: 'paiements/virements',
        title: 'Virements' + titleSuffix,
        redirectTo: 'paiements/finance',
        pathMatch: 'full'
      },
      {
        path: 'parametres/equipe',
        title: 'Équipe admin' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/parametres/platform-team/platform-team.component').then(m => m.PlatformTeamComponent)
      },
      {
        path: 'parametres/equipe/nouveau',
        title: 'Inviter un admin' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/parametres/platform-team/platform-team-create.component').then(
            m => m.PlatformTeamCreateComponent
          )
      },
      {
        // Modifier un admin : même formulaire que l'invitation (30/10/2026).
        path: 'parametres/equipe/:uuid',
        title: 'Modifier un admin' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/parametres/platform-team/platform-team-create.component').then(
            m => m.PlatformTeamCreateComponent
          )
      },
      // Communications plateforme
      {
        path: 'communications',
        children: [
          { path: '', redirectTo: 'messages', pathMatch: 'full' },
          {
            path: 'messages',
            title: 'Communications' + titleSuffix,
            loadComponent: () =>
              import('./views/admin/communications/communications-list.component').then(m => m.CommunicationsListComponent),
            data: { communicationsMode: 'soft' },
          },
          {
            path: 'messages/new',
            title: 'Nouvelle communication' + titleSuffix,
            loadComponent: () =>
              import('./views/admin/communications/communication-editor.component').then(m => m.CommunicationEditorComponent),
          },
          {
            path: 'messages/:uuid/edit',
            title: 'Modifier communication' + titleSuffix,
            loadComponent: () =>
              import('./views/admin/communications/communication-editor.component').then(m => m.CommunicationEditorComponent),
          },
          {
            path: 'restrictions',
            title: 'Restrictions' + titleSuffix,
            loadComponent: () =>
              import('./views/admin/communications/restrictions-list.component').then(m => m.RestrictionsListComponent),
            data: { communicationsMode: 'hard' },
          },
          {
            path: 'restrictions/new',
            title: 'Nouvelle restriction' + titleSuffix,
            loadComponent: () =>
              import('./views/admin/communications/restriction-editor.component').then(m => m.RestrictionEditorComponent),
          },
          {
            path: 'restrictions/:uuid/edit',
            title: 'Modifier restriction' + titleSuffix,
            loadComponent: () =>
              import('./views/admin/communications/restriction-editor.component').then(m => m.RestrictionEditorComponent),
          },
          {
            path: 'stats',
            title: 'Stats communications' + titleSuffix,
            loadComponent: () =>
              import('./views/admin/communications/communications-stats.component').then(m => m.CommunicationsStatsComponent),
          },
        ],
      },
      // Peekship
      {
        path: 'peekship/partenaires',
        title: 'Peekship partenaires' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/peekship/peekship-partners-list.component').then(m => m.PeekshipPartnersListComponent),
      },
      {
        path: 'peekship/partenaires/:uuid',
        title: 'Peekship partenaire' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/peekship/peekship-partner-detail.component').then(m => m.PeekshipPartnerDetailComponent),
      },
      {
        path: 'peekship/invitations',
        title: 'Peekship invitations' + titleSuffix,
        loadComponent: () =>
          import('./views/admin/peekship/peekship-invites.component').then(m => m.PeekshipInvitesComponent),
      },
      // Régie publicitaire (doc/advertising/admin)
      {
        path: 'advertising/advertisers',
        title: 'Annonceurs' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/advertising/advertisers-list.component').then(m => m.AdvertisersListComponent)
      },
      {
        path: 'advertising/placements',
        title: 'Emplacements publicitaires' + titleSuffix,
        loadComponent: () => import('./views/admin/advertising/advertising-inventory/advertising-inventory.component').then(m => m.AdvertisingInventoryComponent)
      },
      {
        path: 'advertising/advertisers/nouveau',
        title: 'Nouvel annonceur' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/advertising/advertisers/advertiser-upsert/advertiser-upsert.component').then(m => m.AdvertiserUpsertComponent)
      },
      {
        path: 'advertising/advertisers/:uuid',
        title: 'Détail annonceur' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/advertising/advertiser-shell.component').then(m => m.AdvertiserShellComponent),
        data: { backLink: '/admin/advertising/advertisers' },
        children: [
          { path: '', redirectTo: 'advertiser', pathMatch: 'full' },
          {
            path: 'advertiser',
            loadComponent: () => import('@pkg-views/admin/advertising/advertisers/advertiser/advertiser.component').then(m => m.AdvertiserComponent),
            children: [
              { path: '', redirectTo: 'sheet', pathMatch: 'full' },
              { path: 'sheet', loadComponent: () => import('@pkg-views/admin/advertising/advertiser-detail.component').then(m => m.AdvertiserDetailComponent) },
              { path: 'edit', loadComponent: () => import('@pkg-views/admin/advertising/advertisers/advertiser-upsert/advertiser-upsert.component').then(m => m.AdvertiserUpsertComponent) }
            ]
          },
          { path: 'accounts', loadComponent: () => import('@pkg-views/admin/advertising/advertisers/advertiser-accounts/advertiser-accounts.component').then(m => m.AdvertiserAccountsComponent) },
          { path: 'diffusion', loadComponent: () => import('@pkg-views/admin/advertising/advertisers/advertiser-diffusion/advertiser-diffusion.component').then(m => m.AdvertiserDiffusionComponent) },
          { path: 'targets', loadComponent: () => import('@pkg-views/admin/advertising/advertisers/advertiser-diffusion/advertiser-diffusion-targets.component').then(m => m.AdvertiserDiffusionTargetsComponent) },
        ]
      },
      {
        path: 'advertising/advertisers/:uuid/editer',
        title: 'Modifier l\'annonceur' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/advertising/advertiser-edit-redirect.component').then(m => m.AdvertiserEditRedirectComponent)
      },
      {
        path: 'advertising/advertisers/:uuid/sheet',
        title: 'Fiche annonceur' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/advertising/advertiser-legacy-redirect.component').then(m => m.AdvertiserLegacyRedirectComponent),
        data: { tab: 'sheet' }
      },
      {
        path: 'advertising/advertisers/:uuid/edit',
        title: 'Édition annonceur' + titleSuffix,
        loadComponent: () => import('@pkg-views/admin/advertising/advertiser-legacy-redirect.component').then(m => m.AdvertiserLegacyRedirectComponent),
        data: { tab: 'edit' }
      },
      {
        path: 'advertising/campaigns',
        redirectTo: 'advertising/advertisers',
        pathMatch: 'full'
      },
      {
        path: 'advertising/campaigns/:uuid',
        redirectTo: 'advertising/advertisers',
        pathMatch: 'full'
      },
      {
        path: 'stripe-connect',
        redirectTo: 'paiements/sante',
        pathMatch: 'full'
      },
      {
        path: 'informations-legales',
        title: 'Informations légales' + titleSuffix,
        loadComponent: () =>
          import('@pkg-views/admin/platform/legal-identity/platform-legal-identity.component').then(
            m => m.PlatformLegalIdentityComponent
          )
      },
      { path: 'audit', redirectTo: 'informations-legales', pathMatch: 'full' }
    ]
  },
  { path: '', redirectTo: 'admin', pathMatch: 'full' },
  { path: '**', redirectTo: 'admin', pathMatch: 'full' }
];
